import React from "react";

export const CHART_PALETTE = [
  "#A60000",
  "#FFB101",
  "#10B981",
  "#6366F1",
  "#F59E0B",
  "#8B5CF6",
  "#EC4899",
  "#64748B",
];

/** Softer palette for language / category donuts — avoids solid brand-red dominance */
export const LANGUAGE_CHART_COLORS = [
  "#6366F1",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
  "#84CC16",
  "#64748B",
  "#F97316",
  "#7C3AED",
];

export const CHART_COLORS = {
  primary: "#A60000",
  secondary: "#FFB101",
  accent: "#FFB101",
  indigo: "#6366F1",
  emerald: "#10B981",
  violet: "#8B5CF6",
};

export const chartMargin = { top: 60, right: 24, left: 4, bottom: 12 };

export const axisTickStyle = {
  fill: "#667085",
  fontSize: 12,
  fontFamily: "Outfit, system-ui, sans-serif",
};

export const legendStyle = {
  fontSize: 13,
  fontFamily: "Outfit, system-ui, sans-serif",
  color: "#475467",
  paddingTop: 12,
};

export const formatPeriodTick = (value) => {
  if (!value || (typeof value === "string" && !value.trim())) return "";
  const str = String(value);
  const match = str.match(/^(\d{4})-(\d{2})/);
  if (match) {
    const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
    return date.toLocaleString("en", { month: "short", year: "2-digit" });
  }
  if (str.length > 10) return `${str.slice(0, 10)}…`;
  return str;
};

/** Prepends a zero point so single-point line/area charts render a visible trend line */
export const withZeroBaseline = (data, xKey, valueKeys) => {
  if (!Array.isArray(data) || !data.length) return [];
  if (data.length > 1) return data;

  const point = data[0];
  const baseline = { [xKey]: " ", _baseline: true };
  valueKeys.forEach((key) => {
    baseline[key] = 0;
  });

  return [baseline, point];
};

export const getDotProps = (dataLength, color) => ({
  r: dataLength <= 2 ? 7 : 4,
  fill: color,
  stroke: "#ffffff",
  strokeWidth: 2,
});

export const getActiveDotProps = (color) => ({
  r: 8,
  fill: color,
  stroke: "#ffffff",
  strokeWidth: 2,
});

export const CustomTooltip = ({ active, payload, label, valueFormatter }) => {
  if (!active || !payload?.length) return null;
  if (!label || (typeof label === "string" && !label.trim())) return null;

  return (
    <div className="qx-chart-tooltip">
      <p className="qx-chart-tooltip-label">{formatPeriodTick(label) || label}</p>
      <ul className="qx-chart-tooltip-list">
        {payload
          .filter((entry) => entry.value != null)
          .map((entry) => (
            <li key={entry.dataKey}>
              <span className="qx-chart-tooltip-dot" style={{ background: entry.color }} />
              <span className="qx-chart-tooltip-name">{entry.name}</span>
              <span className="qx-chart-tooltip-value">
                {valueFormatter
                  ? valueFormatter(entry.value, entry.name)
                  : entry.value?.toLocaleString?.() ?? entry.value}
              </span>
            </li>
          ))}
      </ul>
    </div>
  );
};

export const currencyTooltipFormatter = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN")}`;

export const ChartGradient = ({ id, color, opacity = 0.28 }) => (
  <defs>
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={color} stopOpacity={opacity} />
      <stop offset="100%" stopColor={color} stopOpacity={0.02} />
    </linearGradient>
  </defs>
);

export const barRadius = [8, 8, 0, 0];

export const defaultXAxis = (dataKey) => ({
  dataKey,
  axisLine: false,
  tickLine: false,
  tick: axisTickStyle,
  tickFormatter: formatPeriodTick,
  dy: 6,
  minTickGap: 20,
  height: 40,
  interval: "preserveStartEnd",
});

export const defaultYAxis = () => ({
  axisLine: false,
  tickLine: false,
  tick: axisTickStyle,
  allowDecimals: false,
  dx: -4,
  width: 48,
  domain: [0, (max) => Math.max(Math.ceil(max * 1.15), 5)],
});

export const defaultLegend = {
  verticalAlign: "top",
  align: "center",
  iconType: "circle",
  iconSize: 8,
  wrapperStyle: { ...legendStyle, top: 0, left: 0, right: 0, paddingTop: 12, paddingBottom: 16 },
};

export const languagePieLegend = {
  layout: "vertical",
  verticalAlign: "middle",
  align: "right",
  iconType: "circle",
  iconSize: 8,
  wrapperStyle: {
    ...legendStyle,
    paddingTop: 0,
    paddingLeft: 12,
    maxHeight: 220,
    overflowY: "auto",
    lineHeight: "22px",
  },
};

export const defaultTooltip = (valueFormatter) => ({
  content: <CustomTooltip valueFormatter={valueFormatter} />,
  cursor: { stroke: "#E4E7EC", strokeWidth: 1, strokeDasharray: "4 4" },
});

export const lineTooltip = (valueFormatter) => ({
  content: <CustomTooltip valueFormatter={valueFormatter} />,
  cursor: { stroke: "#D0D5DD", strokeWidth: 1 },
});
